import { useAudioPlayer } from '@ibsys/kit';
import type { RefObject } from 'react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CONVERSATION_PHRASE_PARAM,
  type ConversationTranscript,
  type DialogAudioStatus,
  PHRASE_TYPES,
  useConversationTranscript,
} from '@/entities/conversations';
import { parseTimeToSeconds } from '@/shared/lib';
import { scrollToTranscriptFragment } from '../lib/scrollToTranscript';
import type { DialogSessionContextValue } from './types';
import { DialogSessionContext } from './context';

const normalizeTimeToSeconds = (time: string | number): number =>
  typeof time === 'number' ? time : parseTimeToSeconds(time);

const getSpokenPhrases = (transcripts: ConversationTranscript[]): ConversationTranscript[] =>
  transcripts.filter((p) => p.type === PHRASE_TYPES.PHRASE && p.person != null);

/** Реплика, накрывающая указанную секунду записи, либо ближайшая к ней. Границы реплик — в миллисекундах. */
const findPhraseAtTime = (
  spokenPhrases: ConversationTranscript[],
  targetSec: number,
): ConversationTranscript | null => {
  if (spokenPhrases.length === 0) return null;
  const targetMs = targetSec * 1000;
  const containing = spokenPhrases.findLast(
    (p) => p.startedAt <= targetMs && (p.finishedAt ?? p.startedAt) >= targetMs,
  );
  if (containing) return containing;
  return spokenPhrases.reduce((prev, curr) =>
    Math.abs(curr.startedAt - targetMs) < Math.abs(prev.startedAt - targetMs) ? curr : prev,
  );
};

type Props = {
  dialogId: string;
  audioStatus?: DialogAudioStatus;
  children: React.ReactNode;
};

export const DialogSession: FC<Props> = ({ dialogId, audioStatus = 'ready', children }) => {
  const [transcriptContainerRef, setTranscriptContainerRef] = useState<RefObject<HTMLElement | null> | null>(null);
  const [autoPlayOnScroll, setAutoPlayOnScroll] = useState(false);
  // По умолчанию транскрипт показывается анонимизированным; неанонимизированный вид — по явному переключению.
  const [showNonAnonymized, setShowNonAnonymized] = useState(false);

  const transcriptsQueryResult = useConversationTranscript(dialogId, !showNonAnonymized);
  const transcripts = transcriptsQueryResult.data ?? [];
  const { state, actions } = useAudioPlayer();

  const transcriptsQuery = useMemo<DialogSessionContextValue['transcriptsQuery']>(
    () => ({
      data: transcripts,
      isSuccess: transcriptsQueryResult.isSuccess,
      isLoading: transcriptsQueryResult.isLoading,
    }),
    [transcripts, transcriptsQueryResult.isSuccess, transcriptsQueryResult.isLoading],
  );

  const registerTranscriptContainer = useCallback((ref: RefObject<HTMLElement | null> | null) => {
    setTranscriptContainerRef(ref);
  }, []);

  const scrollToTranscript = useCallback(
    (messageId: string) => {
      const container = transcriptContainerRef?.current ?? undefined;
      scrollToTranscriptFragment(messageId, transcripts, container);
    },
    [transcripts, transcriptContainerRef],
  );

  // Автоскролл к реплике по URL ?phraseId=... после загрузки транскрипта (spec: phrase-search → экран диалога).
  // Срабатывает один раз на конкретное значение phraseId и при готовности контейнера транскрипта.
  const [searchParams] = useSearchParams();
  const phraseIdParam = searchParams.get(CONVERSATION_PHRASE_PARAM);
  const lastScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!phraseIdParam) return;
    if (!transcriptsQueryResult.isSuccess || transcripts.length === 0) return;
    if (!transcriptContainerRef?.current) return;
    if (lastScrolledRef.current === phraseIdParam) return;
    lastScrolledRef.current = phraseIdParam;
    scrollToTranscriptFragment(phraseIdParam, transcripts, transcriptContainerRef.current);
  }, [phraseIdParam, transcripts, transcriptsQueryResult.isSuccess, transcriptContainerRef]);

  const playFragment = useCallback(
    (fragmentId: string, options?: { scrollTo?: boolean }) => {
      const { scrollTo = true } = options || {};

      const phraseId = Number(fragmentId);
      if (Number.isNaN(phraseId)) return;

      const phrase = transcripts.find((p) => p.id === phraseId);
      if (!phrase) return;

      // Границы реплики приходят в миллисекундах, плеер принимает секунды
      const startSec = phrase.startedAt / 1000;
      actions.seek(startSec);
      actions.play();
      if (scrollTo) {
        scrollToTranscript(fragmentId);
      }
    },
    [transcripts, actions, scrollToTranscript],
  );

  const playAtTime = useCallback(
    (time: string | number, options?: { scrollTo?: boolean }) => {
      const spokenPhrases = getSpokenPhrases(transcripts);
      const phrase = findPhraseAtTime(spokenPhrases, normalizeTimeToSeconds(time));
      if (phrase) playFragment(String(phrase.id), options);
    },
    [transcripts, playFragment],
  );

  const scrollToTime = useCallback(
    (time: string | number, options?: { autoPlay?: boolean }) => {
      const targetSec = normalizeTimeToSeconds(time);
      const spokenPhrases = getSpokenPhrases(transcripts);
      const phrase = findPhraseAtTime(spokenPhrases, targetSec);
      if (!phrase) return;

      const container = transcriptContainerRef?.current ?? undefined;
      scrollToTranscriptFragment(String(phrase.id), transcripts, container);

      const shouldAutoPlay = options?.autoPlay ?? autoPlayOnScroll;
      if (shouldAutoPlay) {
        actions.seek(targetSec);
        actions.play();
      }
    },
    [transcripts, transcriptContainerRef, autoPlayOnScroll, actions],
  );

  const contextValue = useMemo<DialogSessionContextValue>(
    () => ({
      transcriptsQuery,
      transcripts,
      duration: state.duration,
      isPlaying: state.isPlaying,
      audioStatus,
      autoPlayOnScroll,
      setAutoPlayOnScroll,
      playFragment,
      playAtTime,
      scrollToTime,
      scrollToTranscript,
      registerTranscriptContainer,
      pause: actions.pause,
      play: actions.play,
      seek: actions.seek,
      showNonAnonymized,
      setShowNonAnonymized,
    }),
    [
      transcriptsQuery,
      transcripts,
      state.duration,
      state.isPlaying,
      audioStatus,
      autoPlayOnScroll,
      playFragment,
      playAtTime,
      scrollToTime,
      scrollToTranscript,
      registerTranscriptContainer,
      actions.pause,
      actions.play,
      actions.seek,
      showNonAnonymized,
    ],
  );

  return <DialogSessionContext.Provider value={contextValue}>{children}</DialogSessionContext.Provider>;
};
